import { useEffect, useState } from 'react';
import axios from 'axios';
import { TrendingUp, Users, Package, DollarSign, Activity, FileSpreadsheet } from 'lucide-react';
import { MetricCard } from '@/components/Layout';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#06b6d4', '#8b5cf6', '#ec4899', '#10b981', '#f97316'];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, uploadsRes, reportsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/uploads`),
        axios.get(`${API}/reports/sales_by_product`)
      ]);

      setStats(statsRes.data);
      setUploads(uploadsRes.data.slice(0, 5));
      setReportData(reportsRes.data.chart_data || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-400">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Dashboard
        </h1>
        <p className="text-gray-400">Welcome to your analytics command center</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <MetricCard
          title="Total Revenue"
          value={stats?.total_revenue ? `$${stats.total_revenue.toLocaleString()}` : '$0'}
          icon={DollarSign}
          color="cyan"
        />
        <MetricCard
          title="Active Clients"
          value={stats?.clients || 0}
          icon={Users}
          color="purple"
        />
        <MetricCard
          title="Products"
          value={stats?.products || 0}
          icon={Package}
          color="pink"
        />
        <MetricCard
          title="Productivity Score"
          value={stats?.productivity_score ? stats.productivity_score.toFixed(0) : '0'}
          icon={Activity}
          color="green"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 bg-[#18181b] rounded-xl border border-white/10 p-6">
          <h3 className="text-xl font-semibold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Sales by Product
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={reportData.slice(0, 8)}>
              <XAxis dataKey="name" stroke="#6b7280" style={{ fontSize: '12px' }} />
              <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#18181b',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  color: '#fff'
                }}
              />
              <Bar dataKey="value" fill="#06b6d4" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-[#18181b] rounded-xl border border-white/10 p-6">
          <h3 className="text-xl font-semibold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Recent Uploads
          </h3>
          <div className="space-y-3">
            {uploads.length > 0 ? (
              uploads.map((upload, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-3 p-3 rounded-lg bg-[#27272a] border border-white/5"
                  data-testid={`upload-item-${idx}`}
                >
                  <FileSpreadsheet size={20} className="text-cyan-400" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{upload.filename}</p>
                    <p className="text-xs text-gray-400">{upload.records_count} records</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-400">No uploads yet</p>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 bg-[#18181b] rounded-xl border border-white/10 p-6">
        <h3 className="text-xl font-semibold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Top Products Distribution
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={reportData.slice(0, 5)}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              outerRadius={100}
              fill="#8884d8"
              dataKey="value"
            >
              {reportData.slice(0, 5).map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: '#18181b',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                color: '#fff'
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}